// src/components/deck_subdeck/SubdeckGroupContainer.tsx
import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown, MoreHorizontal, Edit, Trash2, FolderOpen, BookOpen } from 'lucide-react';
import { getIconComponent } from './groupIcons';
import SubdeckCard from './SubdeckCard';
import {
    DndContext, closestCenter,
    KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext,
    sortableKeyboardCoordinates, rectSortingStrategy
} from '@dnd-kit/sortable';

export interface SubdeckGroup {
    _id: string;
    name: string;
    description?: string;
    iconType: 'lucide' | 'image';
    iconName: string;
    iconUrl?: string;
    color: string;
    order: number;
    subdeckCount: number;
    cardCount: number;
}

interface Subdeck {
    _id: string;
    name: string;
    description?: string;
    cards: any[];
    cardCount?: number;
    createdAt: string;
    isBlocked?: boolean;
    hasResumo?: boolean;
    isFree?: boolean;
    type?: 'flashcards' | 'apostila';
    groupId?: string | null;
    freeConfig?: { enabled: boolean; freeCardsLimit: number; freeApostilaPagesLimit: number };
}

interface SubdeckGroupContainerProps {
    group: SubdeckGroup | null; // null = container "Sem grupo"
    subdecks: Subdeck[];
    isExpanded: boolean;
    onToggle: () => void;
    isAdmin: boolean;
    isEditor?: boolean;
    canManage: boolean;
    onEditGroup?: () => void;
    onDeleteGroup?: () => void;
    onReorderSubdecks?: (reordered: Subdeck[]) => void;
    onStudyGroup?: () => void;
    // SubdeckCard callbacks
    deckId: string;
    deckName: string;
    deckIconId?: string | null;
    context: any;
    isPurchasedDeck: boolean;
    subdeckStats: Map<string, any>;
    readStatusMap: Map<string, boolean>;
    pageCountMap: Map<string, number>;
    readPercentageMap?: Map<string, number>;
    modeStatsCache: Map<string, any>;
    onStudy: (subdeck: Subdeck) => void;
    onView: (subdeck: Subdeck) => void;
    onEdit: (subdeck: Subdeck) => void;
    onExport: (subdeckId: string) => void;
    onMove: (subdeck: Subdeck) => void;
    onCopy?: (subdeck: Subdeck) => void;
    onDelete: (subdeckId: string) => void;
    onToggleBlock: (subdeckId: string) => void;
    onToggleFree: (subdeckId: string) => void;
    onToggleIntegrarApostila: (subdeckId: string) => void;
    onMarkRead: (subdeckId: string) => void;
    onRename: (subdeckId: string, name: string, desc: string, colorId?: string) => Promise<void>;
    onShowNoResumoModal: (name: string) => void;
    onPrefetch: (subdeck: Subdeck) => void;
    onPrefetchPageData: (subdeck: Subdeck) => void;
    onAssignGroup: (subdeck: Subdeck) => void;
}

const SubdeckGroupContainer: React.FC<SubdeckGroupContainerProps> = ({
    group, subdecks, isExpanded, onToggle,
    isAdmin, isEditor = false, canManage, onEditGroup, onDeleteGroup, onReorderSubdecks, onStudyGroup,
    deckId, deckName, deckIconId, context, isPurchasedDeck,
    subdeckStats, readStatusMap, pageCountMap, readPercentageMap, modeStatsCache,
    onStudy, onView, onEdit, onExport, onMove, onCopy, onDelete,
    onToggleBlock, onToggleFree, onToggleIntegrarApostila, onMarkRead,
    onRename, onShowNoResumoModal, onPrefetch, onPrefetchPageData, onAssignGroup
}) => {
    const [kebabOpen, setKebabOpen] = useState(false);
    const kebabRef = useRef<HTMLDivElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) setKebabOpen(false);
        };
        if (kebabOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [kebabOpen]);

    const isUngrouped = group === null;
    const color = isUngrouped ? '#9CA3AF' : group!.color;
    const name = isUngrouped ? 'Sem grupo' : group!.name;
    const description = isUngrouped ? undefined : group?.description;

    // Converte hex → rgba para o tint suave do header aberto
    const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const groupTint    = hexToRgba(color, 0.04);
    const groupTint6   = hexToRgba(color, 0.06);
    const groupTint10  = hexToRgba(color, 0.10);
    const groupColor70 = hexToRgba(color, 0.70);

    const renderIcon = () => {
        if (isUngrouped) {
            return <FolderOpen size={24} color="#9CA3AF" />;
        }
        if (group!.iconType === 'image' && group!.iconUrl) {
            return (
                <img
                    src={group!.iconUrl}
                    alt={group!.name}
                    style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }}
                />
            );
        }
        const IconComp = getIconComponent(group!.iconName || 'Layers');
        return <IconComp size={26} color={color} />;
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = subdecks.findIndex(s => s._id === active.id);
        const newIndex = subdecks.findIndex(s => s._id === over.id);
        const reordered = arrayMove(subdecks, oldIndex, newIndex);
        onReorderSubdecks?.(reordered);
    };

    return (
        <div
            className={`sdp-group-container ${isExpanded ? 'sdp-group-container--open' : ''} ${isUngrouped ? 'sdp-group-container--ungrouped' : ''}`}
            style={{ '--group-color': color, '--group-tint': groupTint, '--group-tint-6': groupTint6, '--group-tint-10': groupTint10, '--group-color-70': groupColor70 } as React.CSSProperties}
        >
            {/* Header clicável */}
            <div className="sdp-group-header" onClick={onToggle}>
                {/* Ícone */}
                <div className="sdp-group-icon-wrap">
                    {renderIcon()}
                </div>

                {/* Textos */}
                <div className="sdp-group-text">
                    <span className="sdp-group-name">{name}</span>
                    {description && <span className="sdp-group-desc">{description}</span>}
                </div>

                {/* Contador */}
                <span className="sdp-group-counter">
                    <span className="sdp-group-counter-subdecks">{subdecks.length} subdeck{subdecks.length !== 1 ? 's' : ''}</span>
                    <span className="sdp-group-counter-dot">•</span>
                    <span className="sdp-group-counter-cards">{subdecks.reduce((acc, s) => acc + (s.cardCount || 0), 0)} cards</span>
                </span>

                {/* Kebab de gerenciamento (apenas grupos reais, não "Sem grupo") */}
                {!isUngrouped && isAdmin && (
                    <div
                        className="sdp-group-kebab-wrap"
                        ref={kebabRef}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            className="sdp-group-kebab-btn"
                            onClick={e => { e.stopPropagation(); setKebabOpen(p => !p); }}
                            title="Opções do grupo"
                        >
                            <MoreHorizontal size={16} />
                        </button>
                        {kebabOpen && (
                            <div className="sdp-group-kebab-menu">
                                <button className="sdp-group-kebab-item" onClick={() => { setKebabOpen(false); onEditGroup?.(); }}>
                                    <Edit size={14} /> Editar grupo
                                </button>
                                <button className="sdp-group-kebab-item sdp-group-kebab-item--danger" onClick={() => { setKebabOpen(false); onDeleteGroup?.(); }}>
                                    <Trash2 size={14} /> Excluir grupo
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Botão estudar grupo */}
                {onStudyGroup && subdecks.some(s => s.type !== 'apostila') && (
                    <button
                        className="sdp-group-study-btn"
                        onClick={e => { e.stopPropagation(); onStudyGroup(); }}
                        title="Estudar este grupo"
                    >
                        <BookOpen size={14} />
                        <span>Estudar</span>
                    </button>
                )}

                {/* Chevron */}
                <ChevronDown
                    size={18}
                    className={`sdp-group-chevron ${isExpanded ? 'sdp-group-chevron--open' : ''}`}
                />
            </div>

            {/* Corpo expansível */}
            <div className={`sdp-group-body ${isExpanded ? 'expanded' : ''}`}>
                {subdecks.length === 0 ? (
                    <div className="sdp-group-empty">Nenhum subdeck neste grupo.</div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={subdecks.map(s => s._id)} strategy={rectSortingStrategy}>
                            <div className="sdp-grid">
                                {subdecks.map(subdeck => {
                                    const stats = subdeckStats.get(subdeck._id);
                                    const mastery = stats && stats.totalCards > 0 ? stats.masteryPercentage : -1;
                                    const isLocked = !isAdmin && !isPurchasedDeck && !subdeck.freeConfig?.enabled;
                                    return (
                                        <SubdeckCard
                                            key={subdeck._id}
                                            subdeck={subdeck}
                                            isAdmin={isAdmin}
                                            isEditor={isEditor}
                                            canManage={canManage}
                                            masteryPercentage={mastery}
                                            deckId={deckId}
                                            deckName={deckName}
                                            deckIconId={deckIconId}
                                            context={context}
                                            isLockedForUser={isLocked}
                                            isPurchasedDeck={isPurchasedDeck}
                                            hasRead={readStatusMap.get(subdeck._id)}
                                            readPercentage={readPercentageMap?.get(subdeck._id)}
                                            pageCount={pageCountMap.get(subdeck._id)}
                                            groupColor={isUngrouped ? undefined : group!.color}
                                            onStudy={() => onStudy(subdeck)}
                                            onView={() => onView(subdeck)}
                                            onEdit={() => onEdit(subdeck)}
                                            onExport={() => onExport(subdeck._id)}
                                            onMove={() => onMove(subdeck)}
                                            onCopy={onCopy ? () => onCopy(subdeck) : undefined}
                                            onDelete={() => onDelete(subdeck._id)}
                                            onToggleBlock={() => onToggleBlock(subdeck._id)}
                                            onToggleFree={() => onToggleFree(subdeck._id)}
                                            onToggleIntegrarApostila={() => onToggleIntegrarApostila(subdeck._id)}
                                            onMarkRead={() => onMarkRead(subdeck._id)}
                                            onShowNoResumoModal={onShowNoResumoModal}
                                            onPrefetch={() => onPrefetch(subdeck)}
                                            onPrefetchPageData={() => onPrefetchPageData(subdeck)}
                                            onRename={(name, desc, colorId) => onRename(subdeck._id, name, desc, colorId)}
                                            onAssignGroup={() => onAssignGroup(subdeck)}
                                        />
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
            </div>
        </div>
    );
};

export default SubdeckGroupContainer;
